// src/components/CartSidebar.jsx
import { useState } from "react";
import { X, ShoppingCart, Trash2, AlertTriangle, CheckCheck, Wallet, Tag } from "lucide-react";
import Currency from "./Currency";
import api from "../api/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const DISCOUNT_THRESHOLD = 10;
const DISCOUNT_RATE      = 0.15;

export default function CartSidebar({ cart, onClose, onRemoveItem, onClearCart, walletBalance, onPurchaseSuccess }) {
  const navigate = useNavigate();
  const [purchasing,    setPurchasing]    = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchased,     setPurchased]     = useState(false);
  const [discountSaved, setDiscountSaved] = useState(0);

  const itemCount              = cart.length;
  const qualifies              = itemCount >= DISCOUNT_THRESHOLD;
  const originalTotal          = cart.reduce((sum, item) => sum + Number(item.priceNgn || 0), 0);
  const discountAmount         = qualifies ? Math.floor(originalTotal * DISCOUNT_RATE) : 0;
  const finalTotal             = originalTotal - discountAmount;
  const insufficient           = walletBalance < finalTotal;
  const itemsNeededForDiscount = Math.max(0, DISCOUNT_THRESHOLD - itemCount);

  const handlePurchase = async () => {
    if (insufficient || purchasing) return;
    setPurchasing(true);
    setPurchaseError(null);

    try {
      // Build items array — include sujanId for Sujan items
      const items = cart.map(item => ({
        accountId:     item.accountId     || null,
        vaultaccLogId: item.vaultaccLogId || null,
        sujanId:       item.sujanId       || null,
        slotIdx:       item.slotIdx       ?? null,
        source:        item.source,
        title:         item.title,
        category:      item.category,
        priceNgn:      item.priceNgn,
        groupKey:      item.groupKey      || null,
      }));

      const res = await api.post("/social/buy-cart", { items });

      if (res.data.success) {
        setDiscountSaved(res.data.discountAmount || 0);
        setPurchased(true);
        if (res.data.discountApplied) {
          toast.success(`🎉 15% discount applied! You saved ₦${res.data.discountAmount?.toLocaleString()}`);
        } else {
          toast.success(`${res.data.purchases?.length} item(s) purchased!`);
        }
        onPurchaseSuccess(res.data.newBalance);
      } else {
        setPurchaseError(res.data.message || "Purchase failed");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Purchase failed";
      setPurchaseError(msg);
      toast.error(msg);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={!purchased ? onClose : undefined} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <ShoppingCart size={16} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Your Cart</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{itemCount} item{itemCount !== 1 ? "s" : ""} selected</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!purchased && itemCount > 0 && (
              <button onClick={onClearCart} className="text-xs text-red-500 hover:underline px-2 py-1">Clear all</button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {purchased ? (
          // ── Success state ────────────────────────────────────────────────
          <div className="flex-1 overflow-y-auto p-5">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCheck size={28} className="text-green-600 dark:text-green-400" />
              </div>
              <p className="text-base font-bold text-gray-900 dark:text-white">Purchase Complete!</p>
              {discountSaved > 0 && (
                <p className="text-sm text-green-600 dark:text-green-400 mt-1 font-medium">
                  🎉 You saved ₦{discountSaved.toLocaleString()} with the 15% discount!
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Your credentials are in My Purchases.
              </p>
            </div>
            <button onClick={() => { onClearCart(); onClose(); }}
              className="w-full py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition">
              Done
            </button>
          </div>
        ) : itemCount === 0 ? (
          // ── Empty cart ───────────────────────────────────────────────────
          <div className="flex-1 flex flex-col items-center justify-center py-20 text-center px-4">
            <ShoppingCart size={40} className="text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Your cart is empty</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Use the "View" button on any product card, select items, and tap "Add to Cart". Works for all products including VPN and texting apps!
            </p>
          </div>
        ) : (
          <>
            {/* ── Discount banner ──────────────────────────────────────────── */}
            <div className={`mx-4 mt-4 rounded-xl p-3 ${
              qualifies
                ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                : "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
            }`}>
              <div className="flex items-center gap-2">
                <Tag size={14} className={qualifies ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"} />
                {qualifies ? (
                  <p className="text-xs font-semibold text-green-700 dark:text-green-300">
                    🎉 15% discount applied! You're saving ₦{discountAmount.toLocaleString()}
                  </p>
                ) : (
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                    Add {itemsNeededForDiscount} more item{itemsNeededForDiscount !== 1 ? "s" : ""} to unlock 15% discount!
                  </p>
                )}
              </div>
              {!qualifies && (
                <div className="mt-2">
                  <div className="w-full bg-amber-200 dark:bg-amber-800/50 rounded-full h-1.5">
                    <div
                      className="bg-amber-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(100, (itemCount / DISCOUNT_THRESHOLD) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{itemCount}/{DISCOUNT_THRESHOLD} items</p>
                </div>
              )}
            </div>

            {/* ── Cart items ───────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {cart.map((item, index) => (
                <div key={item.cartId || index}
                  className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{item.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {item.category === "google_voice" ? "Google Voice" : item.category === "texting_app" ? "Texting App" : item.category}
                    </p>
                    {item.source === "sujan" && (
                      <p className="text-xs text-indigo-500 dark:text-indigo-400">VPN/App item</p>
                    )}
                    {item.country && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">{item.country}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs font-bold text-purple-600 dark:text-purple-400">
                      <Currency amount={item.priceNgn} />
                    </span>
                    <button onClick={() => onRemoveItem(item.cartId || index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Footer: totals + buy ─────────────────────────────────────── */}
            <div className="px-4 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 space-y-3">
              {purchaseError && (
                <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                  <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-500 dark:text-red-400">{purchaseError}</p>
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Subtotal ({itemCount} items)</span>
                  <span className="text-gray-900 dark:text-white font-medium"><Currency amount={originalTotal} /></span>
                </div>
                {qualifies && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 dark:text-green-400">15% Discount</span>
                    <span className="text-green-600 dark:text-green-400 font-medium">−₦{discountAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold border-t border-gray-200 dark:border-gray-700 pt-2">
                  <span className="text-gray-900 dark:text-white">Total</span>
                  <span className="text-purple-600 dark:text-purple-400"><Currency amount={finalTotal} /></span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400 dark:text-gray-500">Your balance</span>
                  <span className={insufficient ? "text-red-500" : "text-green-600 dark:text-green-400"}>
                    <Currency amount={walletBalance} />
                  </span>
                </div>
              </div>

              {insufficient ? (
                <div>
                  <p className="text-xs text-red-500 text-center mb-2">
                    You need ₦{(finalTotal - walletBalance).toLocaleString()} more to complete this purchase.
                  </p>
                  <button onClick={() => { onClose(); navigate("/dashboard/wallet"); }}
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl transition">
                    <Wallet size={15} /> Fund Wallet
                  </button>
                </div>
              ) : (
                <button onClick={handlePurchase} disabled={purchasing}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl transition">
                  {purchasing
                    ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing...</>
                    : <><ShoppingCart size={15} />Pay <Currency amount={finalTotal} />{qualifies ? " (15% off!)" : ""}</>
                  }
                </button>
              )}

              <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                ⚠️ All sales are final. Credentials shown immediately after payment.
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}